import { useState } from "react";
import { GlassCard } from "./ui/GlassCard";
import { Input } from "./ui/Input";
import { Button } from "./ui/Button";
import { useAuth } from "../lib/auth";
import { useI18n } from "../i18n/useI18n";

/**
 * Global "set a new password" modal.
 *
 * Mounted once at the App root (not tied to /login), because Supabase's
 * password-reset email link redirects back with `#access_token=...&type=
 * recovery` in the URL hash. supabase-js auto-detects that hash, exchanges
 * it for a real session, and fires a `PASSWORD_RECOVERY` auth event (see
 * lib/auth.tsx). That session can land the user on ANY route the redirect
 * URL points to, and — critically — it's a REAL session, so route guards
 * (ProtectedRoute) or Login's own redirect-when-authenticated effect could
 * otherwise whisk the user away before they ever set a new password.
 * Rendering this modal at the App root, driven by `passwordRecoveryActive`,
 * guarantees the "set new password" UI appears on top regardless of route.
 */
export function PasswordRecoveryModal() {
  const { passwordRecoveryActive, dismissPasswordRecovery, updatePassword } = useAuth();
  const { t } = useI18n();
  const c = t.app.login;

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!passwordRecoveryActive) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 6) {
      setError(c.recoveryTooShort);
      return;
    }
    if (password !== confirm) {
      setError(c.recoveryMismatch);
      return;
    }
    setBusy(true);
    try {
      const { error } = await updatePassword(password);
      if (error) setError(error);
      else setSuccess(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-charcoal/50 p-6 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      {/* data-lenis-prevent — see ChartModal.tsx's comment for why every
          fixed-overlay modal needs this on a Lenis-driven page. */}
      <GlassCard data-lenis-prevent className="w-full max-w-sm p-7">
        {success ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <span className="text-3xl text-gold">✦</span>
            <p className="font-display text-lg text-charcoal">{c.recoverySuccess}</p>
            <Button className="mt-2 w-full" onClick={dismissPasswordRecovery}>
              {c.recoveryContinue}
            </Button>
          </div>
        ) : (
          <>
            <p className="eyebrow mb-2">{c.recoveryEyebrow}</p>
            <h3 className="font-display text-xl text-charcoal">{c.recoveryTitle}</h3>
            <p className="mt-2 text-sm text-charcoal/60">{c.recoveryHint}</p>

            <form onSubmit={submit} className="mt-5 flex flex-col gap-3">
              <Input
                id="recovery-password"
                type="password"
                label={c.recoveryNewPassword}
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Input
                id="recovery-confirm"
                type="password"
                label={c.recoveryConfirmPassword}
                required
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
              {error && <p className="text-xs text-red-500">{error}</p>}
              <Button type="submit" className="mt-2 w-full" disabled={busy}>
                {busy ? c.recoverySaving : c.recoverySave}
              </Button>
            </form>
          </>
        )}
      </GlassCard>
    </div>
  );
}
